import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { setDocumentTitle } from '../../utils/documentTitle';
import LanguageSwitcher from '../LanguageSwitcher';

const AngelCareWelcomePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  useEffect(() => {
    setDocumentTitle('', true); // true = isNurseApp
  }, []);

  // ถ้าล็อกอินแล้วให้ redirect ไปหน้า nurse home (ถ้ามี role แล้ว)
  useEffect(() => {
    const checkAndRedirect = async () => {
      if (currentUser && !hasRedirected.current) {
        // ตรวจสอบว่ามี role หรือไม่
        if (currentUser.role) {
          // มี role แล้ว → redirect ไป /nurse
          hasRedirected.current = true;
          navigate('/nurse', { replace: true });
        } else {
          // ยังไม่มี role → redirect ไป /register
          hasRedirected.current = true;
          navigate('/register?complete=true', { 
            replace: true,
            state: {
              fromGoogle: true,
              email: currentUser.email,
              displayName: currentUser.displayName
            }
          });
        }
      }
    };
    
    checkAndRedirect();
  }, [currentUser, navigate]);

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: `linear-gradient(to bottom, ${currentTheme.colors['secondary-bg']}, ${currentTheme.colors.background})`
    }}>
      {/* Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="w-8 h-8 md:w-10 md:h-10 object-contain"
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-400 via-pink-400 to-purple-400 rounded-lg flex items-center justify-center shadow-md hidden">
                <span className="text-xl md:text-2xl">👼</span>
              </div>
              <h1 className="text-lg md:text-xl font-bold" style={{
                background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                NurseApp
              </h1>
            </div>

            {/* Auth Buttons & Language Switcher */}
            <div className="flex items-center space-x-2 md:space-x-3">
              <LanguageSwitcher />
              <Link
                to="/login"
                className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium text-gray-700 hover:text-gray-900 transition"
              >
                {t('common.login')}
              </Link>
              <Link
                to="/register"
                className="px-2 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-medium text-white rounded-lg transition shadow-md"
                style={{
                  background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                {t('common.register')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-14 md:pt-20">
        {/* Hero Section */}
        <section className="px-3 md:px-4 pt-2 md:pt-8 pb-3 md:pb-12 flex-shrink-0">
          <div className="container mx-auto">
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {/* Hero Background with Gradient and Vector Pattern */}
              <div 
                className="relative min-h-[500px] md:min-h-[600px] overflow-hidden py-8 md:py-12"
                style={{
                  background: `linear-gradient(135deg, #1e3a8a 0%, #3b82f6 25%, #60a5fa 50%, #93c5fd 75%, #dbeafe 100%)`
                }}
              >
                {/* Elegant Vector Pattern - Blue and White */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800">
                    {/* Geometric Pattern */}
                    <defs>
                      <pattern id="geometric-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                        <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                        <circle cx="20" cy="20" r="1.5" fill="white" opacity="0.2"/>
                        <circle cx="80" cy="80" r="1.5" fill="white" opacity="0.2"/>
                        <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                        <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                      </pattern>
                      <pattern id="wave-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                        <path d="M0,100 Q50,50 100,100 T200,100" stroke="white" strokeWidth="1" fill="none" opacity="0.15"/>
                        <path d="M0,150 Q50,100 100,150 T200,150" stroke="white" strokeWidth="1" fill="none" opacity="0.1"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#geometric-pattern)"/>
                    <rect width="100%" height="100%" fill="url(#wave-pattern)"/>
                    
                    {/* Floating Circles */}
                    <circle cx="100" cy="150" r="60" fill="white" opacity="0.05"/>
                    <circle cx="1100" cy="650" r="80" fill="white" opacity="0.05"/>
                    <circle cx="600" cy="100" r="40" fill="white" opacity="0.08"/>
                    <circle cx="900" cy="300" r="50" fill="white" opacity="0.06"/>
                    <circle cx="200" cy="600" r="70" fill="white" opacity="0.04"/>
                    
                    {/* Elegant Lines */}
                    <line x1="0" y1="200" x2="1200" y2="200" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="400" x2="1200" y2="400" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="600" x2="1200" y2="600" stroke="white" strokeWidth="1" opacity="0.1"/>
                  </svg>
                </div>
                
                {/* Subtle Overlay Gradient */}
                <div className="absolute inset-0" style={{
                  background: `linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)`
                }}></div>

                {/* Floating Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-10 left-10 text-white/20 text-6xl animate-pulse">👼</div>
                  <div className="absolute top-32 right-20 text-white/20 text-5xl animate-pulse delay-1000">🩺</div>
                  <div className="absolute bottom-20 left-20 text-white/20 text-4xl animate-pulse delay-2000">❤️</div>
                  <div className="absolute bottom-32 right-10 text-white/20 text-6xl animate-pulse delay-3000">✨</div>
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-3 md:px-4 text-white py-4 md:py-8">
                  <div className="mb-4 md:mb-6">
                    {/* Logo with enhanced styling */}
                    <div className="relative inline-block">
                      <img 
                        src="/logo.png" 
                        alt="NurseApp Logo" 
                        className="mx-auto mb-2 md:mb-4 object-contain logo-hero"
                        style={{ 
                          width: 'clamp(180px, 45vw, 270px)',
                          height: 'clamp(180px, 45vw, 270px)',
                          maxWidth: '270px',
                          maxHeight: '270px',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          filter: 'drop-shadow(0 10px 30px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 50px rgba(37, 99, 235, 0.3))',
                          animation: 'logoFloat 6s ease-in-out infinite'
                        }}
                        onError={(e) => {
                          // Fallback to emoji if image not found
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="mx-auto mb-4 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30 hidden" style={{
                        width: 'clamp(180px, 45vw, 270px)',
                        height: 'clamp(180px, 45vw, 270px)',
                        maxWidth: '270px',
                        maxHeight: '270px'
                      }}>
                        <span className="text-6xl md:text-7xl lg:text-8xl">🩺</span>
                      </div>
                    </div>
                  </div>
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 drop-shadow-lg">
                    NurseApp
                  </h2>
                  <p className="text-base md:text-xl lg:text-2xl mb-2 md:mb-4 drop-shadow-md opacity-95 font-medium">
                    ระบบจัดการพยาบาลและผู้ช่วยพยาบาล
                  </p>
                  <p className="text-sm md:text-lg lg:text-xl mb-2 md:mb-4 drop-shadow-md opacity-90">
                    บริหารจัดการเวร • อุปกรณ์ • ทีม • Gamification
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="px-3 md:px-4 py-2 md:py-12 flex-shrink-0">
          <div className="container mx-auto text-center">
            <h3 className="text-lg md:text-3xl lg:text-4xl font-bold mb-1 md:mb-4" style={{ color: currentTheme.colors['text-primary'] }}>
              แพลตฟอร์มสำหรับ
            </h3>
            <p className="text-xs md:text-lg lg:text-xl mb-2 md:mb-8" style={{ color: currentTheme.colors['text-secondary'] }}>
              พยาบาล • ผู้ช่วยพยาบาล • หมอ • เจ้าหน้าที่
            </p>
            <p className="text-xs md:text-base lg:text-lg max-w-2xl mx-auto" style={{ color: currentTheme.colors['text-secondary'] }}>
              จัดการทีม • วางแผนเวร • ตรวจสอบอุปกรณ์ • Gamification • Marketplace
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-3 md:px-4 py-2 md:py-8 flex-shrink-0">
          <div className="container mx-auto max-w-md">
            <div className="flex flex-col space-y-2 md:space-y-4">
              {/* Primary CTA - Nurse Registration */}
              <Link
                to="/register"
                className="w-full py-2.5 md:py-4 px-4 md:px-6 text-white text-sm md:text-lg font-semibold rounded-lg md:rounded-xl transition shadow-lg transform hover:scale-105 text-center"
                style={{
                  background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                สมัครสมาชิก (สำหรับพยาบาล)
              </Link>

              {/* Secondary CTA */}
              <Link
                to="/login"
                className="w-full py-2.5 md:py-4 px-4 md:px-6 bg-white text-sm md:text-lg font-semibold rounded-lg md:rounded-xl transition text-center"
                style={{
                  border: `2px solid ${currentTheme.colors.primary}`,
                  color: currentTheme.colors.primary
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = currentTheme.colors['secondary-bg'];
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                {t('common.login')}
              </Link>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="px-3 md:px-4 py-3 md:py-12 flex-shrink-0" style={{
          background: `linear-gradient(to bottom, ${currentTheme.colors.background}, ${currentTheme.colors['secondary-bg']})`
        }}>
          <div className="container mx-auto">
            <h3 className="text-xl md:text-3xl font-bold mb-4 md:mb-8 text-center" style={{ color: currentTheme.colors['text-primary'] }}>
              Features หลัก
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {/* Feature 1 - Shift Management */}
              <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:scale-105" style={{ backgroundColor: currentTheme.colors.background }}>
                <div className="text-3xl md:text-5xl mb-2 md:mb-4">📅</div>
                <h4 className="text-xs md:text-lg font-semibold mb-1 md:mb-2" style={{ color: currentTheme.colors['text-primary'] }}>จัดการเวร</h4>
                <p className="text-[10px] md:text-sm hidden md:block" style={{ color: currentTheme.colors['text-secondary'] }}>
                  วางแผนเวร เข้า-ออกเวร แลกเวร
                </p>
              </div>

              {/* Feature 2 - Equipment Management */}
              <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:scale-105" style={{ backgroundColor: currentTheme.colors.background }}>
                <div className="text-3xl md:text-5xl mb-2 md:mb-4">🩺</div>
                <h4 className="text-xs md:text-lg font-semibold mb-1 md:mb-2" style={{ color: currentTheme.colors['text-primary'] }}>อุปกรณ์</h4>
                <p className="text-[10px] md:text-sm hidden md:block" style={{ color: currentTheme.colors['text-secondary'] }}>
                  ตรวจสอบอุปกรณ์ ยืม-คืน ส่งซ่อม
                </p>
              </div>

              {/* Feature 3 - Team Management */}
              <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:scale-105" style={{ backgroundColor: currentTheme.colors.background }}>
                <div className="text-3xl md:text-5xl mb-2 md:mb-4">👥</div>
                <h4 className="text-xs md:text-lg font-semibold mb-1 md:mb-2" style={{ color: currentTheme.colors['text-primary'] }}>ทีม</h4>
                <p className="text-[10px] md:text-sm hidden md:block" style={{ color: currentTheme.colors['text-secondary'] }}>
                  สร้างทีม จัดการสมาชิก
                </p>
              </div>

              {/* Feature 4 - Gamification */}
              <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md text-center hover:shadow-lg transition transform hover:scale-105" style={{ backgroundColor: currentTheme.colors.background }}>
                <div className="text-3xl md:text-5xl mb-2 md:mb-4">🏆</div>
                <h4 className="text-xs md:text-lg font-semibold mb-1 md:mb-2" style={{ color: currentTheme.colors['text-primary'] }}>Gamification</h4>
                <p className="text-[10px] md:text-sm hidden md:block" style={{ color: currentTheme.colors['text-secondary'] }}>
                  Points, Level, Badges
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Features Section */}
        <section className="px-3 md:px-4 py-3 md:py-8 flex-shrink-0" style={{ backgroundColor: currentTheme.colors.background }}>
          <div className="container mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {/* Marketplace */}
              <div className="p-4 md:p-6 rounded-lg md:rounded-xl shadow-md text-center" style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}>
                <div className="text-3xl md:text-4xl mb-2">🛒</div>
                <h4 className="text-sm md:text-lg font-semibold mb-2" style={{ color: currentTheme.colors['text-primary'] }}>Marketplace</h4>
                <p className="text-xs md:text-sm" style={{ color: currentTheme.colors['text-secondary'] }}>
                  สินค้าสำหรับพยาบาล
                </p>
              </div>

              {/* Gift System */}
              <div className="p-4 md:p-6 rounded-lg md:rounded-xl shadow-md text-center" style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}>
                <div className="text-3xl md:text-4xl mb-2">🎁</div>
                <h4 className="text-sm md:text-lg font-semibold mb-2" style={{ color: currentTheme.colors['text-primary'] }}>ของขวัญ</h4>
                <p className="text-xs md:text-sm" style={{ color: currentTheme.colors['text-secondary'] }}>
                  ให้ของขวัญเป็นกำลังใจ
                </p>
              </div>

              {/* Mascot Ranking */}
              <div className="p-4 md:p-6 rounded-lg md:rounded-xl shadow-md text-center" style={{ backgroundColor: currentTheme.colors['secondary-bg'] }}>
                <div className="text-3xl md:text-4xl mb-2">👼</div>
                <h4 className="text-sm md:text-lg font-semibold mb-2" style={{ color: currentTheme.colors['text-primary'] }}>Mascot Ranking</h4>
                <p className="text-xs md:text-sm" style={{ color: currentTheme.colors['text-secondary'] }}>
                  Level Up พร้อม Mascot
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-3 md:px-4 py-3 md:py-8 border-t flex-shrink-0 mt-auto" style={{ 
          backgroundColor: currentTheme.colors.background,
          borderColor: currentTheme.colors['border-color']
        }}>
          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-1 md:space-y-0 md:space-x-6 text-[10px] md:text-sm" style={{ color: currentTheme.colors['text-secondary'] }}>
              <Link to="/terms" className="transition hover:opacity-80" style={{ color: currentTheme.colors.primary }}>
                ข้อกำหนดการใช้งาน
              </Link>
              <span className="hidden md:inline" style={{ color: currentTheme.colors['border-color'] }}>|</span>
              <Link to="/privacy" className="transition hover:opacity-80" style={{ color: currentTheme.colors.primary }}>
                นโยบายความเป็นส่วนตัว
              </Link>
              <span className="hidden md:inline" style={{ color: currentTheme.colors['border-color'] }}>|</span>
              <Link to="/contact" className="transition hover:opacity-80" style={{ color: currentTheme.colors.primary }}>
                ติดต่อเรา
              </Link>
            </div>
            <div className="text-center mt-2 md:mt-4 text-[9px] md:text-xs" style={{ color: currentTheme.colors['text-secondary'] }}>
              © 2025 NurseApp. สงวนลิขสิทธิ์
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AngelCareWelcomePage;

// Add CSS animation for logo
const logoStyles = `
  @keyframes logoFloat {
    0%, 100% {
      transform: translateY(0px) rotate(0deg);
    }
    25% {
      transform: translateY(-10px) rotate(2deg);
    }
    50% {
      transform: translateY(-20px) rotate(0deg);
    }
    75% {
      transform: translateY(-10px) rotate(-2deg);
    }
  }
  
  .logo-hero {
    animation: logoFloat 6s ease-in-out infinite;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = logoStyles;
  if (!document.head.querySelector('style[data-logo-animation]')) {
    styleSheet.setAttribute('data-logo-animation', 'true');
    document.head.appendChild(styleSheet);
  }
}

